import {
  Box,
  Button,
  Divider,
  Group,
  Popover,
  Stack,
  Switch,
  Text
} from "@mantine/core";
import { IconColumnsOff } from "@tabler/icons-react";
import type {
  MRT_Column,
  MRT_RowData,
  MRT_TableInstance,
  MRT_VisibilityState
} from "mantine-react-table-open";
import { useCallback, useId, useMemo, useState } from "react";
import { Userpilot } from "userpilot";

type VisibilityUpdater =
  MRT_VisibilityState | ((prev: MRT_VisibilityState) => MRT_VisibilityState);

interface ColumnGroup {
  label: string;
  children: ReadonlyArray<string>;
}

interface HideableColumnState {
  id: string;
  label: string;
  isVisible: boolean;
}

interface ColumnGroupState extends ColumnGroup {
  id: string;
  isChecked: boolean;
  isPartiallyChecked: boolean;
  hasAnyVisibleChild: boolean;
}

interface ColumnVisibilityMenuProps<TData extends MRT_RowData> {
  table: MRT_TableInstance<TData>;
  columnVisibility?: Record<string, boolean>;
  onColumnVisibilityChange?: (updater: VisibilityUpdater) => void;
  columnGroups?: Record<string, ColumnGroup>;
}

function getColumnHeaderLabel<TData extends MRT_RowData>(
  column: MRT_Column<TData>
): string {
  const meta = column.columnDef.meta as
    { visibilityLabel?: string } | undefined;
  if (meta?.visibilityLabel) {
    return meta.visibilityLabel;
  }
  const header = column.columnDef.header;
  if (typeof header === "string" && header.trim().length > 0) {
    return header;
  }

  if (typeof column.columnDef.accessorKey === "string") {
    return column.columnDef.accessorKey;
  }

  return column.id;
}

export function ColumnVisibilityMenu<TData extends MRT_RowData>({
  table,
  columnVisibility,
  onColumnVisibilityChange,
  columnGroups = {}
}: ColumnVisibilityMenuProps<TData>) {
  const [opened, setOpened] = useState(false);
  const buttonId = useId();

  const visibilityState = columnVisibility ?? table.getState().columnVisibility;

  const handleVisibilityChange = useCallback(
    (updater: VisibilityUpdater) => {
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(updater);
        return;
      }

      if (typeof updater === "function") {
        table.setColumnVisibility(updater);
        return;
      }

      table.setColumnVisibility((prev) => ({
        ...prev,
        ...updater
      }));
    },
    [onColumnVisibilityChange, table]
  );

  const hideableColumnData = useMemo<Array<HideableColumnState>>(
    () =>
      table
        .getAllLeafColumns()
        .filter((col) => col.getCanHide())
        .map((col) => ({
          id: col.id,
          label: getColumnHeaderLabel(col),
          isVisible: visibilityState[col.id] !== false
        })),
    [table, visibilityState]
  );

  const columnGroupStates = useMemo<Array<ColumnGroupState>>(() => {
    return Object.entries(columnGroups).map(([groupId, group]) => {
      const childVisibility = group.children.map(
        (childId) => visibilityState[childId] !== false
      );
      const visibleCount = childVisibility.filter(Boolean).length;
      const totalChildren = group.children.length;
      const isChecked =
        totalChildren > 0 ? visibleCount === totalChildren : true;
      const isPartiallyChecked =
        totalChildren > 0 && visibleCount > 0 && visibleCount < totalChildren;

      return {
        id: groupId,
        label: group.label,
        children: group.children,
        isChecked,
        isPartiallyChecked,
        hasAnyVisibleChild: visibleCount > 0
      };
    });
  }, [columnGroups, visibilityState]);

  const isAnyColumnVisible = useMemo(() => {
    const anyHideableVisible = hideableColumnData.some(
      (column) => column.isVisible
    );
    const anyGroupVisible = columnGroupStates.some(
      (group) => group.hasAnyVisibleChild
    );
    return anyHideableVisible || anyGroupVisible;
  }, [hideableColumnData, columnGroupStates]);

  const areAllColumnsVisible = useMemo(() => {
    const allHideableVisible = hideableColumnData.every(
      (column) => column.isVisible
    );
    const allGroupVisible = columnGroupStates.every((group) => group.isChecked);
    return allHideableVisible && allGroupVisible;
  }, [hideableColumnData, columnGroupStates]);

  const handleToggleAllColumns = (visible: boolean) => {
    const updates: Record<string, boolean> = {};
    hideableColumnData.forEach((column) => {
      updates[column.id] = visible;
    });
    Object.keys(columnGroups).forEach((groupId) => {
      updates[groupId] = visible;
    });
    handleVisibilityChange(updates);
  };

  const handleGroupToggle = (groupId: string, checked: boolean) => {
    const group = columnGroups[groupId];
    if (group) {
      const updates: Record<string, boolean> = {
        [groupId]: checked
      };
      group.children.forEach((childId) => {
        updates[childId] = checked;
      });
      handleVisibilityChange(updates);
    }
  };

  const handleColumnToggle = (columnId: string, checked: boolean) => {
    handleVisibilityChange({ [columnId]: checked });
  };

  return (
    <Popover opened={opened} position="bottom-start" onChange={setOpened}>
      <Popover.Target>
        <Button
          aria-expanded={opened}
          id={buttonId}
          leftSection={<IconColumnsOff size={18} />}
          size="sm"
          styles={{
            root: {
              padding: "1px 4px",
              height: "auto"
            }
          }}
          variant="subtle"
          onClick={() => {
            Userpilot.track("em_spalten_verwalten_button");
            setOpened(!opened);
          }}
        >
          Spalten verwalten
        </Button>
      </Popover.Target>
      <Popover.Dropdown
        aria-labelledby={buttonId}
        mah={400}
        miw={300}
        role="dialog"
        style={{ overflowY: "auto" }}
      >
        <Stack gap="md">
          <Group justify="space-between">
            <Button
              disabled={!isAnyColumnVisible}
              size="xs"
              variant="subtle"
              onClick={() => handleToggleAllColumns(false)}
            >
              Alles ausblenden
            </Button>
            <Button
              disabled={areAllColumnsVisible}
              size="xs"
              variant="subtle"
              onClick={() => handleToggleAllColumns(true)}
            >
              Alles anzeigen
            </Button>
          </Group>
          {columnGroupStates.length > 0 && (
            <>
              <Divider />
              <Box>
                <Text c="dimmed" mb="xs" size="xs">
                  Default Ansichten
                </Text>
                <Stack gap="xs">
                  {columnGroupStates.map((group) => (
                    <Group justify="space-between" key={group.id} px="xs">
                      <Text size="sm">{group.label}</Text>
                      <Switch
                        aria-checked={
                          group.isPartiallyChecked ? "mixed" : group.isChecked
                        }
                        aria-label={`${group.label} anzeigen`}
                        checked={group.isChecked}
                        size="sm"
                        onChange={(event) =>
                          handleGroupToggle(
                            group.id,
                            event.currentTarget.checked
                          )
                        }
                      />
                    </Group>
                  ))}
                </Stack>
              </Box>
            </>
          )}
          {hideableColumnData.length > 0 && <Divider />}
          <Box>
            <Text c="dimmed" mb="xs" size="xs">
              Spalten
            </Text>
            <Stack gap="xs">
              {hideableColumnData.map((column) => (
                <Group justify="space-between" key={column.id} px="xs">
                  <Text size="sm">{column.label}</Text>
                  <Switch
                    aria-label={`${column.label} anzeigen`}
                    checked={column.isVisible}
                    size="sm"
                    onChange={(event) =>
                      handleColumnToggle(column.id, event.currentTarget.checked)
                    }
                  />
                </Group>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
